import React, { useEffect } from "react"
import { Form } from "antd"
import { useTranslation } from "react-i18next"
import { FormInstance } from "antd/lib/form"
import { DateTime } from "luxon"
import LuxonDatePicker from "@/modules/shared/LuxonDatePicker"
import { BaseInfo, PanelMode } from "rc-picker/lib/interface"

const { RangePicker } = LuxonDatePicker

interface DayTimeRangeProps {
  name: string
  label: string
  timeWindow?: {
    from?: DateTime
    to?: DateTime
  }
  form: FormInstance
  setFieldsChange: (_: boolean) => void
  disabled?: boolean
}

export const DayTimeRangeForm: React.FC<DayTimeRangeProps> = ({
  name,
  timeWindow,
  form,
  setFieldsChange,
  disabled,
  label,
}) => {
  const { t } = useTranslation()
  // defaultFrom and defaultTo DateTime
  // are used to set the default value of the date picker
  // from is 00:00 and to is 23:59
  const defaultFrom = DateTime.fromObject({ hour: 0, minute: 0 })
  const defaultTo = DateTime.fromObject({ hour: 23, minute: 59 })

  const initialFrom = timeWindow?.from ?? defaultFrom
  const initialTo = timeWindow?.to ?? defaultTo
  const initialRange: [DateTime, DateTime] | null =
    timeWindow && (timeWindow.from || timeWindow.to) ? [initialFrom, initialTo] : null
  const [timeWindowValue, setTimeWindowValue] = React.useState<[DateTime, DateTime] | null>(
    initialRange
  )

  const validateTimeRange = (_: any, value: { from: DateTime; to: DateTime } | null) => {
    if (value && value.from && value.to && value.from.toMillis() > value.to.toMillis()) {
      return Promise.reject(new Error(t("common.validation.startBeforeEnd")))
    }
    return Promise.resolve()
  }

  useEffect(() => {
    if (timeWindowValue) {
      form.setFieldsValue({
        expectedArrival: {
          from: timeWindowValue?.[0] ?? undefined,
          to: timeWindowValue?.[1] ?? undefined,
        },
      })
      form.validateFields([name])
    }
  }, [timeWindowValue])

  const handleDropoffByTimeByPanel = (
    range: [DateTime, DateTime],
    info: BaseInfo & {
      source: "reset" | "panel"
      mode: [PanelMode, PanelMode]
    }
  ) => {
    if (!range || !info || !info.source || !info.mode) {
      return
    }

    const [from, to] = range

    if (!from && !to) {
      return
    }

    const fromTime = from ?? defaultFrom
    const toTime = to ?? defaultTo

    setTimeWindowValue([fromTime, toTime])
    setFieldsChange(true)
  }

  return (
    <Form.Item
      name={name}
      label={label || t("common.arriveBy")}
      rules={[
        {
          validator: validateTimeRange,
          message: t("common.validation.startBeforeEnd"),
        },
      ]}
    >
      <>
        <div className="flex items-center mt-1">
          <RangePicker
            name={name}
            size="middle"
            picker="time"
            popupStyle={{ zIndex: 3000 }}
            showSecond={false}
            minuteStep={15}
            // disabledTime={disabledTime(tz)}
            value={[timeWindowValue?.[0], timeWindowValue?.[1]]}
            onPickerValueChange={handleDropoffByTimeByPanel}
            placeholder={["Start Time", "End Time"]}
            format={"HH:mm"}
            allowClear={false}
            hideDisabledOptions={true}
            disabled={disabled}
            needConfirm={false}
          />
        </div>
      </>
    </Form.Item>
  )
}
